import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Database, Download, FileArchive, FileSpreadsheet, Loader2, RotateCcw, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { OnOffSwitch } from "@/components/OnOffSwitch";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/Field";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { useSignatures } from "@/lib/signatures";
import { buildReportsZip, saveBlob } from "@/lib/exportData";

const thisYear = new Date().getFullYear();
const yearList = (earliest) => {
  const start = Math.min(2020, earliest || 2020);
  return Array.from({ length: thisYear - start + 1 }, (_, i) => String(thisYear - i));
};

function Card({ icon: Icon, title, text, children, testid }) {
  return (
    <section className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--primary))] text-white"><Icon className="h-5 w-5" /></span>
        <div><h3 className="font-heading text-lg font-bold text-slate-900">{title}</h3><p className="text-sm text-slate-500">{text}</p></div>
      </div>
      <div className="mt-auto flex flex-wrap items-end gap-2">{children}</div>
    </section>
  );
}

function DlButton({ busy, onClick, testid }) {
  return <Button onClick={onClick} disabled={busy} data-testid={testid}>{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}{busy ? "Preparing..." : "Download"}</Button>;
}

function WeeklyBackups() {
  const { settings, reload } = useApp();
  const [list, setList] = useState([]);
  const load = useCallback(() => api.get("/backups").then((r) => setList(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (v) => { try { await api.put("/settings", { backup: { enabled: v } }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const runNow = async () => { try { await api.post("/backups/run"); toast.success("Backup started. It will appear here in a moment and an email is on its way."); setTimeout(load, 4000); } catch (e) { toast.error(errMsg(e)); } };
  const get = async (b) => { try { const r = await api.get(`/backups/${b.id}`, { responseType: "blob" }); saveBlob(r.data, b.original_filename); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-5" data-testid="weekly-backups-card">
      <div className="flex flex-wrap items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-700 text-white"><ShieldCheck className="h-5 w-5" /></span>
        <div className="flex-1"><h3 className="font-heading text-lg font-bold text-slate-900">Weekly backups</h3>
          <p className="text-sm text-slate-500">Every Sunday at 2:00 AM (Central) a full database backup is saved here and the Master Admin gets an email. Kept for 90 days. Master Admin only.</p></div>
        <OnOffSwitch checked={settings?.backup?.enabled !== false} onChange={toggle} testid="backup-enabled-toggle" />
        <Button variant="outline" onClick={runNow} data-testid="backup-run-now-btn">Back up now</Button>
      </div>
      <div className="divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="backup-list">
        {list.length === 0 && <p className="p-4 text-center text-sm text-slate-500">No backups yet.</p>}
        {list.map((b) => (
          <div key={b.id} className="flex items-center justify-between gap-3 px-4 py-2 text-sm" data-testid={`backup-row-${b.id}`}>
            <span className="font-semibold text-slate-800">{b.original_filename}</span>
            <span className="text-slate-500">{fmtDate(b.created_at)} · {Math.round(b.size / 1024)} KB</span>
            <Button size="sm" variant="ghost" onClick={() => get(b)} data-testid={`backup-download-${b.id}`}><Download className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
    </section>
  );
}

function RestoreBackup() {
  const [file, setFile] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("confirm", confirm);
    try {
      const r = await api.post("/backups/restore", fd);
      const n = Object.values(r.data.restored).reduce((a, b) => a + b, 0);
      toast.success(`Backup restored (${n} items).${r.data.needs_reset ? ` ${r.data.needs_reset} user(s) need a password reset.` : ""} Reloading...`);
      setTimeout(() => window.location.reload(), 2500);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <section className="space-y-3 rounded-xl border-2 border-amber-300 bg-amber-50 p-5" data-testid="restore-backup-card">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-600 text-white"><RotateCcw className="h-5 w-5" /></span>
        <div><h3 className="font-heading text-lg font-bold text-slate-900">Restore from backup</h3>
          <p className="text-sm text-slate-700">Upload a MWJK backup file (.json or .json.gz). <b>All current data is replaced</b> with the data in the backup. A safety backup of today's data is saved first, so you can undo this. Passwords stay the same for existing users; uploaded receipt photos and attachments are not changed.</p></div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input type="file" accept=".json,.gz,application/json,application/gzip" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm text-slate-700" data-testid="restore-file-input" />
        <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Type RESTORE" className="h-10 w-40 bg-white text-slate-900" data-testid="restore-confirm-input" />
        <Button onClick={run} disabled={!file || confirm.trim().toUpperCase() !== "RESTORE" || busy} className="bg-amber-700 hover:bg-amber-800" data-testid="restore-btn">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}{busy ? "Restoring..." : "Restore"}
        </Button>
      </div>
    </section>
  );
}

export default function AdminExport() {
  const ctx = useApp();
  const sig = useSignatures();
  const [year, setYear] = useState(String(thisYear));
  const [busy, setBusy] = useState("");
  const [years, setYears] = useState(yearList());
  useEffect(() => {
    api.get("/export/years").then((r) => setYears(yearList(r.data.earliest))).catch(() => {});
  }, []);
  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); toast.success("Download ready"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const server = (path, name) => run(path, async () => {
    const r = await api.get(path, { responseType: "blob" });
    saveBlob(r.data, name);
  });
  const day = new Date().toISOString().slice(0, 10);
  return (
    <div className="space-y-4" data-testid="admin-export">
      <p className="text-on-bg-muted text-sm">Download your app data to this device. Passwords and login sessions are never included.</p>
      <div className="grid gap-4 lg:grid-cols-3">
        <Card icon={FileArchive} title="Export All Reports" text="Annual Summary, Financial Summary and every Conference Summary for the chosen year, each as PDF and Excel, in one zip file." testid="export-reports-card">
          <div className="w-28"><NativeSelect value={year} onChange={setYear} options={years} testid="export-reports-year" /></div>
          <DlButton busy={busy === "reports"} testid="export-reports-btn" onClick={() => run("reports", async () => saveBlob(await buildReportsZip(ctx, year, sig), `MWJK-Reports-${year}.zip`))} />
        </Card>
        <Card icon={FileSpreadsheet} title="Export All Records" text="Tithes, Conference Collection, Conference Registrations and Members/Users as Excel files in one zip file." testid="export-records-card">
          <DlButton busy={busy === "/export/records"} testid="export-records-btn" onClick={() => server("/export/records", `MWJK-Records-${day}.zip`)} />
        </Card>
        <Card icon={Database} title="Export Full Database" text="A complete backup of all app data (settings, churches, records, messages, finance) in one JSON file." testid="export-database-card">
          <DlButton busy={busy === "/export/database"} testid="export-database-btn" onClick={() => server("/export/database", `MWJK-Backup-${day}.json`)} />
        </Card>
      </div>
      {ctx.isFullAdmin && <WeeklyBackups />}
      {ctx.isFullAdmin && <RestoreBackup />}
    </div>
  );
}
